import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link, useRouteMatch } from 'react-router-dom';
import { Flex, HStack, Text, Tooltip } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { FeatureFlags } from '../../../../../components/Authorizer';
import JobStatusIcon from '../../../../../components/JobStatusIcon';
import { analyticsIds } from '../../../../../constants/analyticsIds';
import { ReportsPageDuskIds } from '../../../../../constants/duskIds';
import { selectCompanyFeatures } from '../../../../../redux/modules/company';
import { getProjectStatusFromRequirementStatus } from '../../../../../utils/statusUtil';

const JobInfoCell = ({ value: { data: insuranceRequirements } }) => {
  const { url } = useRouteMatch();
  const companyFeatures = useSelector(selectCompanyFeatures);

  const itemsEnabled = useMemo(() => companyFeatures.find(cf => cf.name === FeatureFlags.Items)?.isEnabled, [companyFeatures]);

  const records = useMemo(() => {
    // if a company has items enables, then only use the first record's insuredRecord
    if (itemsEnabled && insuranceRequirements.length > 0) {
      const itemRecord = insuranceRequirements.find(iRs => ('item' in iRs));

      const insuredRecordData = itemRecord
        ? itemRecord?.insuredInsuranceRequirement
        : insuranceRequirements.find(iRs => !('item' in iRs)) ?? {};

      return [{ ...insuredRecordData, job: insuredRecordData?.job ?? insuranceRequirements[0]?.job }];
    }

    return insuranceRequirements;
  }, [itemsEnabled, insuranceRequirements]);

  return records.map(({ job, ...requirement }) => (
    (
      <Flex direction="column" justifyContent="center" mt={1}>
        <Link
          data-analytics-id={analyticsIds.reportsPage.openProjectLink}
          data-dusk-value={requirement?.status}
          dusk={ReportsPageDuskIds.getRecordStatusById(requirement?.id)}
          rel="noopener noreferrer"
          style={{
            textDecoration: 'none',
            _hover: { textDecoration: 'none' }
          }}
          target="_blank"
          to={url.replace(/reports/, `projects/${job?.id}`)}
        >
          <HStack alignItems="center">
            <JobStatusIcon status={getProjectStatusFromRequirementStatus(requirement.status)} />
            <HStack _hover={{ fontWeight: '500' }} whiteSpace="nowrap">
              <Text color="greyScale.900">{job?.name}</Text>
              {job?.number && (
              <Tooltip label="Project Number">
                <Text
                  color="greyScale.800"
                  fontSize="sm"
                  fontWeight="400"
                  textAlign="left"
                >
                  {`(${job.number})`}
                </Text>
              </Tooltip>
              )}
              {!itemsEnabled && requirement?.insuranceRequirement?.contractNumber && (
              <Tooltip label="Record Number">
                <Text
                  color="greyScale.800"
                  fontSize="sm"
                  fontWeight="400"
                  textAlign="left"
                >
                  {`(${requirement.insuranceRequirement.contractNumber})`}
                </Text>
              </Tooltip>
              )}
            </HStack>
          </HStack>
        </Link>
      </Flex>
    )
  ));
};

JobInfoCell.propTypes = {
  value: PropTypes.shape({
    name: PropTypes.string,
    number: PropTypes.string,
    status: PropTypes.string
  })
};

export default JobInfoCell;
